// Plan 24c Phase B, Task 9 — How to Play screen (Screen 32)
//
// Full-viewport overlay with 4 tabs: Board Game / Items / Minigames / Controls.
// The user can navigate between tabs and return to warmup via Back button or ESC.
//
// Design tokens (docs/design/tokens.json):
//   typography.family.headline = "'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif"
//   color.ui.overlay-dim       = "rgba(0, 0, 0, 0.60)"
//   z-index.overlay            = 250

import { createTabBar } from '@client/ui/components/tab-bar';
import { createButton } from '@client/ui/components/button';
import { createPanel } from '@client/ui/components/panel';
import type { UIComponentHandle } from '@client/ui/components/types';
import { allItems, type ItemTargetType } from '@shared/game/items';
import type { ScreenHandle, ScreenMountContext } from './types';
import { getInputModality } from '../../input/modality';

// ─────────────────────────────────────────────────────────────────────────────
// Public types
// ─────────────────────────────────────────────────────────────────────────────

export type HowToPlayTabId = 'board-game' | 'items' | 'minigames' | 'controls';

export type HowToPlayPayload = {
  /** Which tab to show on mount. Default 'board-game'. */
  initialTab?: HowToPlayTabId;
  /** Default: router.navigate('warmup-arena-overlay', { portal: null }). */
  onBack?: () => void;
};

// ─────────────────────────────────────────────────────────────────────────────
// Tab content renderer
// ─────────────────────────────────────────────────────────────────────────────

function targetTypeLabel(targetType: ItemTargetType): string {
  switch (targetType) {
    case 'self':
      return 'Self cast';
    case 'player':
      return 'Player target';
    case 'tile':
      return 'Tile target';
    case 'direction':
      return 'Directional skillshot';
    case 'none':
      return 'No target';
  }
}

function renderIconList(rows: readonly (readonly [string, string])[]): HTMLElement {
  const ol = document.createElement('ol');
  ol.style.cssText = ['margin: 0', 'padding: 0', 'list-style: none'].join('; ');

  rows.forEach(([icon, text]) => {
    const li = document.createElement('li');
    li.style.cssText = [
      'display: flex',
      'align-items: flex-start',
      'gap: 10px',
      'padding: 6px 0',
    ].join('; ');

    const iconSpan = document.createElement('span');
    iconSpan.style.cssText = 'font-size: 20px; width: 28px; text-align: center; flex-shrink: 0';
    iconSpan.textContent = icon;

    const textSpan = document.createElement('span');
    textSpan.textContent = text;

    li.append(iconSpan, textSpan);
    ol.appendChild(li);
  });

  return ol;
}

function renderTabContent(tabId: HowToPlayTabId): HTMLElement {
  const container = document.createElement('div');
  container.style.cssText = [
    'padding: 16px 0',
    "font-family: 'Nunito Sans', 'Nunito', 'Arial Rounded MT Bold', sans-serif",
    'color: #FFFFFF',
    'font-size: 15px',
    'line-height: 1.6',
  ].join('; ');

  switch (tabId) {
    case 'board-game': {
      const steps: [string, string][] = [
        ['🎲', 'Roll dice, choose routes at forks, and move tile by tile.'],
        ['🔑', 'Collect keys from gold tiles and dropped-key piles.'],
        ['🏆', 'Buy trophies at treasure chests when you can afford the cost.'],
        ['✊', 'Use items at the start of your turn before rolling.'],
        ['🧿', 'Claim soul anchors; rivals who land there lose keys or HP.'],
        ['💀', 'At 0 HP you drop keys, respawn at a graveyard, and keep playing.'],
        ['⭐', 'Between-round minigames can award extra rewards when enabled.'],
      ];
      container.appendChild(renderIconList(steps));
      break;
    }

    case 'items': {
      const intro = document.createElement('p');
      intro.style.cssText = 'margin: 0 0 12px 0; color: #B0B8C8';
      intro.textContent =
        'Items are collected from item tiles, weapons caches, minigames, and other effects. Use them at the start of your turn before rolling.';
      container.appendChild(intro);

      const list = document.createElement('div');
      list.style.cssText = [
        'display: grid',
        'grid-template-columns: repeat(auto-fit, minmax(240px, 1fr))',
        'gap: 10px',
      ].join('; ');
      for (const item of allItems()) {
        const card = document.createElement('section');
        card.style.cssText = [
          'padding: 10px',
          'border-radius: 8px',
          'background: rgba(255,255,255,0.08)',
          'border: 1px solid rgba(255,255,255,0.12)',
        ].join('; ');
        const h4 = document.createElement('h4');
        h4.style.cssText = [
          'margin: 0 0 4px',
          "font-family: 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif",
          'font-size: 16px',
          'color: #E8A020',
        ].join('; ');
        h4.textContent = item.name;
        const meta = document.createElement('div');
        meta.style.cssText =
          'margin-bottom: 5px; color: #FFFFFF; font-size: 12px; font-weight: 800';
        meta.textContent = `${item.category.toUpperCase()} • ${item.rarity.toUpperCase()} • ${targetTypeLabel(item.targetType)}${item.blockedByStoneSkin ? ' • Blocked by Stone Skin' : ''}`;
        const desc = document.createElement('p');
        desc.style.cssText = 'margin: 0; color: #B0B8C8; line-height: 1.35';
        desc.textContent = item.description;
        card.append(h4, meta, desc);
        list.appendChild(card);
      }
      container.appendChild(list);
      break;
    }

    case 'minigames': {
      const intro = document.createElement('p');
      intro.style.cssText = 'margin: 0 0 16px 0; color: #B0B8C8';
      intro.textContent =
        'When enabled by room settings, players compete in quick minigames between board rounds for extra rewards.';
      container.appendChild(intro);

      const tags: [string, string, string][] = [
        ['FFA', 'Free-for-all — every player for themselves.', '#CC3333'],
        ['Teams', 'Players split into two competing teams.', '#2255CC'],
        ['Survival', 'Last player standing wins the reward.', '#228833'],
      ];

      const tagsRow = document.createElement('div');
      tagsRow.style.cssText = 'display: flex; flex-wrap: wrap; gap: 12px';

      tags.forEach(([label, desc, color]) => {
        const wrapper = document.createElement('div');
        wrapper.style.cssText = 'display: flex; align-items: center; gap: 8px';

        const tag = document.createElement('span');
        tag.style.cssText = [
          `background: ${color}`,
          'color: #FFFFFF',
          'border-radius: 4px',
          'padding: 2px 10px',
          "font-family: 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif",
          'font-size: 14px',
          'font-weight: 700',
          'letter-spacing: 1px',
        ].join('; ');
        tag.textContent = label;

        const descSpan = document.createElement('span');
        descSpan.style.cssText = 'color: #B0B8C8; font-size: 13px';
        descSpan.textContent = desc;

        wrapper.appendChild(tag);
        wrapper.appendChild(descSpan);
        tagsRow.appendChild(wrapper);
      });

      container.appendChild(tagsRow);
      break;
    }

    case 'controls': {
      const grid = document.createElement('div');
      grid.style.cssText = [
        'display: grid',
        'grid-template-columns: repeat(auto-fit, minmax(180px, 1fr))',
        'gap: 16px',
      ].join('; ');

      const keyboardColumn: [string, [string, string][]] = [
        'KEYBOARD',
        [
          ['Move', 'WASD'],
          ['Route / Target', 'WASD / Arrows'],
          ['Confirm', 'Space / Enter'],
          ['Items', 'E'],
          ['Emotes', 'Hold T + Mouse'],
        ],
      ];
      const gamepadColumn: [string, [string, string][]] = [
        'GAMEPAD',
        [
          ['Move', 'Left Stick'],
          ['Route / Target', 'Left Stick / D-pad'],
          ['Confirm', 'A / Cross'],
          ['Cancel target', 'B / Circle'],
          ['Items', 'RB / R1'],
          ['Emotes', 'Hold LT + Right Stick'],
        ],
      ];
      const touchColumn: [string, [string, string][]] = [
        'TOUCH / MOUSE',
        [
          ['Move', 'Tap tiles'],
          ['Routes', 'Tap arrows'],
          ['Items', 'Tap Items / targets'],
          ['Emotes', 'Tap emote button'],
        ],
      ];
      const modality = getInputModality();
      const columns: [string, [string, string][]][] =
        modality === 'touch'
          ? [touchColumn]
          : modality === 'gamepad'
            ? [gamepadColumn, touchColumn]
            : [keyboardColumn, gamepadColumn, touchColumn];

      columns.forEach(([header, rows]) => {
        const col = document.createElement('div');

        const h5 = document.createElement('h5');
        h5.style.cssText = [
          'margin: 0 0 10px 0',
          "font-family: 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif",
          'font-size: 16px',
          'color: #E8A020',
          'text-transform: uppercase',
          'letter-spacing: 1px',
        ].join('; ');
        h5.textContent = header;
        col.appendChild(h5);

        rows.forEach(([action, key]) => {
          const row = document.createElement('div');
          row.style.cssText = [
            'display: flex',
            'justify-content: space-between',
            'align-items: center',
            'padding: 5px 0',
            'border-bottom: 1px solid rgba(255,255,255,0.07)',
          ].join('; ');

          const actionSpan = document.createElement('span');
          actionSpan.style.cssText = 'color: #B0B8C8';
          actionSpan.textContent = action;

          const keySpan = document.createElement('span');
          keySpan.style.cssText = [
            'color: #FFFFFF',
            'font-weight: 700',
            "font-family: 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif",
            'font-size: 14px',
          ].join('; ');
          keySpan.textContent = key;

          row.appendChild(actionSpan);
          row.appendChild(keySpan);
          col.appendChild(row);
        });

        grid.appendChild(col);
      });

      container.appendChild(grid);
      break;
    }
  }

  return container;
}

// ─────────────────────────────────────────────────────────────────────────────
// Tab ID → index mapping
// ─────────────────────────────────────────────────────────────────────────────

const TAB_IDS: HowToPlayTabId[] = ['board-game', 'items', 'minigames', 'controls'];

function tabIdToIndex(id: HowToPlayTabId): number {
  const idx = TAB_IDS.indexOf(id);
  return idx >= 0 ? idx : 0;
}

// ─────────────────────────────────────────────────────────────────────────────
// Factory
// ─────────────────────────────────────────────────────────────────────────────

export function createHowToPlayScreen(ctx: ScreenMountContext<HowToPlayPayload>): ScreenHandle {
  const { host, gsapRegistry, router, payload } = ctx;
  const { initialTab = 'board-game', onBack } = payload;

  let disposed = false;
  const componentHandles: UIComponentHandle[] = [];

  // ── Default back action ───────────────────────────────────────────────────

  function doBack(): void {
    if (onBack !== undefined && onBack !== null) {
      onBack();
    } else {
      router.navigate('warmup-arena-overlay', { portal: null });
    }
  }

  // ── Build root overlay ────────────────────────────────────────────────────

  const root = document.createElement('div');
  root.setAttribute('data-screen', 'how-to-play');
  root.style.cssText = [
    'position: fixed',
    'inset: 0',
    'z-index: 930',
    'background: rgba(0,0,0,0.60)',
    'display: flex',
    'align-items: center',
    'justify-content: center',
    'box-sizing: border-box',
    'padding: max(12px, var(--app-safe-area-top)) max(12px, var(--app-safe-area-right)) max(12px, var(--app-safe-area-bottom)) max(12px, var(--app-safe-area-left))',
  ].join('; ');

  // ── Build panel using createPanel ─────────────────────────────────────────

  const panelHandle = createPanel({ gsapRegistry, padding: 'lg' });
  componentHandles.push(panelHandle);

  const panelEl = panelHandle.element;
  panelEl.style.cssText += [
    '; width: min(700px, calc(var(--app-viewport-width, 100vw) - 24px))',
    'max-height: calc(var(--app-viewport-height, 100vh) - 24px)',
    'overflow-y: auto',
    'overscroll-behavior: contain',
    'box-sizing: border-box',
  ].join('; ');

  // ── Top row: Back | Title | Spacer ────────────────────────────────────────

  const topRow = document.createElement('div');
  topRow.style.cssText = [
    'display: flex',
    'justify-content: space-between',
    'align-items: center',
    'gap: 12px',
    'margin-bottom: 12px',
  ].join('; ');

  // Back button
  const backHandle = createButton({
    gsapRegistry,
    variant: 'ghost',
    size: 'small',
    label: '← BACK',
    onClick: () => {
      if (!disposed) doBack();
    },
  });
  componentHandles.push(backHandle);

  // Title
  const titleEl = document.createElement('h3');
  titleEl.style.cssText = [
    "font-family: 'Fredoka One', 'Baloo 2', 'Arial Black', sans-serif",
    'font-size: clamp(18px, 5vw, 28px)',
    'color: white',
    'margin: 0',
    'text-align: center',
    'line-height: 1.05',
    'min-width: 0',
  ].join('; ');
  titleEl.textContent = 'SMASHPARTY - HOW TO PLAY';

  // Spacer (mirrors back button width for visual balance)
  const spacer = document.createElement('div');
  spacer.style.cssText = 'visibility: hidden';
  spacer.setAttribute('aria-hidden', 'true');

  topRow.appendChild(backHandle.element);
  topRow.appendChild(titleEl);
  topRow.appendChild(spacer);

  panelEl.appendChild(topRow);

  // ── Tab bar ───────────────────────────────────────────────────────────────

  const initialTabIndex = tabIdToIndex(initialTab);
  const tabBarHandle = createTabBar({
    gsapRegistry,
    tabs: [
      { label: 'BOARD GAME', id: 'board-game' },
      { label: 'ITEMS', id: 'items' },
      { label: 'MINIGAMES', id: 'minigames' },
      { label: 'CONTROLS', id: 'controls' },
    ],
    activeIndex: initialTabIndex,
    onChange: (_, id) => {
      if (!disposed) switchTab(id as HowToPlayTabId);
    },
  });
  componentHandles.push(tabBarHandle);
  panelEl.appendChild(tabBarHandle.element);

  // ── Content area ──────────────────────────────────────────────────────────

  const contentArea = document.createElement('div');
  contentArea.setAttribute('data-tab-content', '');
  contentArea.style.cssText = 'margin-top: 16px; min-height: 200px';
  panelEl.appendChild(contentArea);

  // ── switchTab ─────────────────────────────────────────────────────────────

  function switchTab(id: HowToPlayTabId): void {
    // Remove existing children
    while (contentArea.firstChild) {
      contentArea.removeChild(contentArea.firstChild);
    }
    contentArea.appendChild(renderTabContent(id));
  }

  // Render initial tab
  switchTab(initialTab);

  // ── Mount to root ─────────────────────────────────────────────────────────

  root.appendChild(panelEl);
  host.appendChild(root);

  // ── ESC handler ───────────────────────────────────────────────────────────

  function handleEsc(e: KeyboardEvent): void {
    if (e.key !== 'Escape') return;
    if (!disposed) doBack();
  }

  document.addEventListener('keydown', handleEsc);

  // ── dispose ───────────────────────────────────────────────────────────────

  function dispose(): void {
    if (disposed) return;
    disposed = true;

    document.removeEventListener('keydown', handleEsc);

    for (const h of componentHandles) {
      h.dispose();
    }

    if (root.parentNode) {
      root.parentNode.removeChild(root);
    }
  }

  return { element: root, dispose };
}
